"use client";

import { Capacitor } from "@capacitor/core";
import { shareViaSystemSheet } from "@/lib/native/share";
import type { ShareQrKind } from "./targets";

export type ShareQrRef = { kind: ShareQrKind; id: number | string };

export function shareQrUrl(
  ref: ShareQrRef,
  options: { download?: boolean; size?: number } = {},
) {
  const params = new URLSearchParams({ kind: ref.kind, id: String(ref.id) });
  if (options.size) params.set("size", String(options.size));
  if (options.download) params.set("download", "1");
  return `/api/share-qr?${params.toString()}`;
}

function filenameFrom(response: Response) {
  const header = response.headers.get("content-disposition") ?? "";
  return /filename="([^"]+)"/.exec(header)?.[1] ?? "hiive-qr.png";
}

/**
 * Save the print-grade PNG. A browser gets a real download; the native shell
 * cannot download from a web view, so it hands the file to the system share
 * sheet, whose "Save Image" puts it in Photos.
 */
export async function downloadShareQr(ref: ShareQrRef) {
  const response = await fetch(shareQrUrl(ref, { download: true }));
  if (!response.ok)
    throw new Error((await response.text()) || "QR unavailable");
  const filename = filenameFrom(response);
  const blob = await response.blob();
  if (Capacitor.isNativePlatform()) {
    const result = await shareViaSystemSheet({
      asset: {
        file: new File([blob], filename, { type: "image/png" }),
        type: "png",
      },
      dialogTitle: "Save QR code",
    });
    if (!result.ok && result.reason !== "aborted")
      throw new Error("Couldn't open the share sheet");
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
